<script setup lang="ts">
    import ImageEditForm from '@/components//ImageEditForm.vue';
    import ElementInput from '@/components/ElementInput.vue';
    import BaseIcon from '@/components/BaseIcon.vue';
    import useConfigStore from '@/store/config.store';
    import useProductStore from '@/store/product.store';
    import useField from '@/composables/useField';
    import { t } from '@/translations/i18n';
    import { storeToRefs } from 'pinia';
    import { computed } from 'vue';
    import type { IImageField } from '@/typescript/interfaces/IImageField';

    const { field } = defineProps<{
        field: IImageField;
    }>();

    const productStore = useProductStore();

    const { isAdmin } = storeToRefs(useConfigStore());

    const { isFormVisible, showForm, deleteField, toggleLock, hideForm, duplicateField } = useField(field.id);

    function onImageUpload(uploadedImageUrl?: string, uploadedImageName?: string, uploadedImageUUID?: string) {
        const fieldToEdit = { ...field };
        fieldToEdit.url = uploadedImageUrl;
        fieldToEdit.filename = uploadedImageName;
        fieldToEdit.uuid = uploadedImageUUID;
        productStore.updateField(fieldToEdit);
    }

    const warningMessage = computed(() => {
        if (!field.hasQualityWarning) {
            return undefined;
        }
        return t('image_quality_warning');
    });
</script>
<template>
    <ElementInput
        :model-value="field.filename"
        deletable
        :lockable="isAdmin"
        as-button
        editable
        :duplicable="!!field.url"
        :locked="!field.editable"
        :placeholder="$t('default_image_placeholder')"
        :warning="true"
        :warning-message
        :delete-title="$t('deleteImage')"
        :edit-title="$t('editImage')"
        :duplicate-title="$t('duplicateImage')"
        @lock="toggleLock(field)"
        @edit="showForm"
        @click="showForm"
        @delete="deleteField(field)"
        @duplicate="duplicateField(field)"
    >
        <template #thumbnail>
            <span class="sr-only">{{ $t('photo_icon') }}</span>
            <BaseIcon
                type="outlined"
                name="PhotoIcon"
                class="h-6 w-6 text-text"
                :title="$t('image')"
            />
            <img
                v-if="field.url"
                :title="$t('image')"
                :src="field.url"
                class="max-h-8"
                :alt="$t('thumbnail_image')"
            />
        </template>
    </ElementInput>
    <Teleport
        v-if="isFormVisible"
        to="#formContainer"
    >
        <ImageEditForm
            class="pointer-events-auto"
            :field
            :warning="true"
            :warning-message
            @image-upload="onImageUpload"
            @close="hideForm"
        />
    </Teleport>
</template>
